import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Monitor, ExternalLink, KeyRound, Search, Power, Lock, SlidersHorizontal } from "lucide-react";
import EntityConfigDialog from "@/components/admin/EntityConfigDialog";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const toNum = (v) => (v === "" || v == null ? 0 : Number(v));
const EMPTY = { name: "", code: "", banca_id: "", agencia_id: "", hardware_serial: "", address: "", active: true, commission_pct: 0, sales_limit_bs: null, enable_web_access: false, kiosco_enabled: false, kiosco_pin: "", kiosco_pago_movil_info: "" };

const fmtLimit = (v) => (!v || Number(v) <= 0) ? "Ilimitado" : `${Number(v).toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Bs`;

export default function TaquillasPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [toggleTarget, setToggleTarget] = useState(null);
  const [configFor, setConfigFor] = useState(null);

  const { data: bancas = [] } = useQuery({
    queryKey: ["bancas"],
    queryFn: async () => (await api.get("/bancas")).data,
  });
  const { data: taquillas = [], isLoading } = useQuery({
    queryKey: ["taquillas"],
    queryFn: async () => (await api.get("/taquillas")).data,
  });
  const { data: agencias = [] } = useQuery({
    queryKey: ["agencias"],
    queryFn: async () => (await api.get("/agencias")).data,
  });
  const agenciaMap = Object.fromEntries(agencias.map((a) => [a.id, a]));

  const bancaMap = Object.fromEntries(bancas.map((b) => [b.id, b]));

  const save = useMutation({
    mutationFn: async (raw) => {
      // Comisión/límite se heredan de la agencia: alta con defaults limpios, edición conserva lo existente
      const payload = editing
        ? { ...raw, commission_pct: toNum(editing.commission_pct), sales_limit_bs: editing.sales_limit_bs ?? null }
        : { ...raw, commission_pct: 0, sales_limit_bs: null, enable_web_access: false };
      if (editing) return (await api.patch(`/taquillas/${editing.id}`, payload)).data;
      return (await api.post("/taquillas", payload)).data;
    },
    onSuccess: () => {
      toast.success(editing ? "Taquilla actualizada" : "Taquilla creada");
      qc.invalidateQueries({ queryKey: ["taquillas"] });
      setOpen(false);
      setEditing(null);
      setForm(EMPTY);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al guardar"),
  });

  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/taquillas/${id}`)).data,
    onSuccess: () => {
      toast.success("Taquilla eliminada");
      qc.invalidateQueries({ queryKey: ["taquillas"] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Error al eliminar"),
  });

  const toggleStatus = useMutation({
    mutationFn: async (id) => (await api.patch(`/taquillas/${id}/toggle-status`)).data,
    onSuccess: (t) => { toast.success(`Taquilla ${t.active ? "habilitada" : "deshabilitada"}`); qc.invalidateQueries({ queryKey: ["taquillas"] }); setToggleTarget(null); },
    onError: (e) => { toast.error(e?.response?.data?.detail || "Error"); setToggleTarget(null); },
  });

  const filtered = taquillas.filter((t) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (t.name || "").toLowerCase().includes(q) || (t.code || "").toLowerCase().includes(q);
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...EMPTY, banca_id: bancas[0]?.id || "" });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name, code: t.code, banca_id: t.banca_id, agencia_id: t.agencia_id || "", hardware_serial: t.hardware_serial || "",
      address: t.address || "", active: t.active, commission_pct: t.commission_pct || 0, sales_limit_bs: t.sales_limit_bs || 0,
      enable_web_access: false,
      kiosco_enabled: !!t.kiosco_enabled,
      kiosco_pin: "",  // vacío por seguridad — solo se envía si el admin lo cambia
      kiosco_pago_movil_info: t.kiosco_pago_movil_info || "",
    });
    setOpen(true);
  };

  const kioscoUrl = (t) => `${window.location.origin}/kiosco/${t.id}`;
  const copyKioscoUrl = async (t) => {
    try {
      await navigator.clipboard.writeText(kioscoUrl(t));
      toast.success("Enlace del kiosco copiado");
    } catch { toast.error("No se pudo copiar"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Taquillas</h1>
          <p className="text-sm text-muted-foreground">Puntos físicos de venta asociados a una banca.</p>
        </div>
        <Button onClick={openCreate} data-testid="taquilla-create-button"><Plus size={16} className="mr-1.5"/>Nueva Taquilla</Button>
      </div>

      <div className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"/>
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar por nombre o código…" className="pl-9" data-testid="taquilla-search-input"/>
      </div>

      <Card className="fintech-card overflow-hidden" data-testid="admin-table-taquillas">
        <div className="overflow-x-auto">
          <table className="w-full text-sm fintech-table">
            <thead>
              <tr className="text-xs uppercase tracking-wide text-muted-foreground text-left">
                <th className="px-4 py-3">Código</th>
                <th className="px-4 py-3">Nombre</th>
                <th className="px-4 py-3">Banca</th>
                <th className="px-4 py-3">Agencia</th>
                <th className="px-4 py-3">Serial</th>
                <th className="px-4 py-3">Dirección</th>
                <th className="px-4 py-3 text-right">Límite Diario</th>
                <th className="px-4 py-3">Kiosco</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && Array.from({ length: 4 }).map((_, i) => (
                <tr key={`sk-${i}`} className="fintech-row" data-testid="taquilla-skeleton-row">
                  <td colSpan={10} className="px-4 py-3"><Skeleton className="h-6 w-full"/></td>
                </tr>
              ))}
              {!isLoading && filtered.length === 0 && <tr><td colSpan={10} className="px-4 py-8 text-center text-muted-foreground">Sin taquillas registradas.</td></tr>}
              {!isLoading && filtered.map((t) => (
                <tr key={t.id} className="fintech-row">
                  <td className="px-4 py-3 font-mono text-sky-600 dark:text-sky-300">{t.code}</td>
                  <td className="px-4 py-3 font-medium">{t.name}</td>
                  <td className="px-4 py-3 text-muted-foreground">{bancaMap[t.banca_id]?.name || "-"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{agenciaMap[t.agencia_id]?.name || "-"}</td>
                  <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{t.hardware_serial || "-"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{t.address || "-"}</td>
                  <td className="px-4 py-3 text-right tabular-nums" data-testid={`taquilla-limit-${t.code}`}>{fmtLimit(t.sales_limit_bs)}</td>
                  <td className="px-4 py-3">
                    {t.kiosco_enabled ? (
                      <div className="inline-flex items-center gap-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs border-2 border-primary/40 text-primary bg-primary/10 font-semibold">
                          <Monitor size={12} className="mr-1"/> Activo
                        </span>
                        <Button variant="ghost" size="icon" onClick={() => copyKioscoUrl(t)} title="Copiar enlace" data-testid={`taquilla-copy-kiosco-${t.code}`}>
                          <ExternalLink size={14}/>
                        </Button>
                      </div>
                    ) : (
                      <span className="text-[11px] font-semibold text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border-2 font-semibold ${t.active ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10" : "border-rose-500/40 text-rose-300 bg-rose-500/10"}`}>
                      {t.active ? "Activa" : "Inactiva"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => setConfigFor(t)} title="Cupos y comisiones" data-testid={`taquilla-config-${t.code}`}><SlidersHorizontal size={16}/></Button>
                      <Button variant="ghost" size="icon" onClick={() => openEdit(t)} data-testid={`taquilla-edit-${t.code}`}><Pencil size={16}/></Button>
                      <Button variant="ghost" size="icon" className={t.active ? "text-amber-300" : "text-emerald-300"} title={t.active ? "Deshabilitar" : "Habilitar"} onClick={() => setToggleTarget(t)} data-testid={`taquilla-toggle-${t.code}`}><Power size={16}/></Button>
                      <Button variant="ghost" size="icon" className="text-rose-300" onClick={() => { if (window.confirm(`Eliminar ${t.name}?`)) del.mutate(t.id); }} data-testid={`taquilla-delete-${t.code}`}><Trash2 size={16}/></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border">
          <DialogHeader><DialogTitle>{editing ? "Editar taquilla" : "Nueva taquilla"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Nombre</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="taquilla-form-name"/>
            </div>
            <div className="space-y-2">
              <Label>Código</Label>
              <Input value={form.code} onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})} data-testid="taquilla-form-code"/>
            </div>
            <div className="space-y-2">
              <Label>Agencia</Label>
              <Select value={form.agencia_id || "none"} onValueChange={(v) => setForm({...form, agencia_id: v === "none" ? "" : v})}>
                <SelectTrigger data-testid="taquilla-form-agencia"><SelectValue placeholder="Sin agencia"/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin agencia</SelectItem>
                  {agencias.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} ({a.code})</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-muted-foreground">La banca se asocia automáticamente desde la agencia.</p>
            </div>
            <div className="space-y-2">
              <Label>Serial del terminal {editing && <span className="text-[11px] text-muted-foreground">(no editable)</span>}</Label>
              <Input value={form.hardware_serial} onChange={(e) => setForm({...form, hardware_serial: e.target.value})} placeholder="SN del equipo físico" data-testid="taquilla-form-hardware" readOnly={!!editing} disabled={!!editing}/>
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label>Dirección</Label>
              <Input value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} />
            </div>
            <div className="flex items-center gap-2 md:col-span-2">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({...form, active: v})} id="active-taq" />
              <Label htmlFor="active-taq">Activa</Label>
            </div>

            {/* Kiosco de autogestión */}
            <div className="md:col-span-2 mt-2 rounded-xl border-2 border-primary/30 bg-primary/5 p-3 space-y-3">
              <div className="flex items-center gap-2">
                <Monitor size={16} className="text-primary"/>
                <div className="text-sm font-bold uppercase tracking-widest text-primary">Modo Kiosco</div>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.kiosco_enabled}
                  onCheckedChange={(v) => setForm({...form, kiosco_enabled: v})}
                  id="kiosco-enabled"
                  data-testid="taquilla-kiosco-toggle"
                />
                <Label htmlFor="kiosco-enabled">Habilitar autogestión sin operador</Label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="space-y-1">
                  <Label className="flex items-center gap-1.5 text-xs"><KeyRound size={12}/> PIN de kiosco {editing ? "(dejar vacío para no cambiar)" : "(obligatorio)"}</Label>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    inputMode="numeric"
                    maxLength={8}
                    value={form.kiosco_pin}
                    onChange={(e) => setForm({...form, kiosco_pin: e.target.value.replace(/\D/g, "")})}
                    placeholder="4-8 dígitos"
                    data-testid="taquilla-kiosco-pin"
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Notas internas (opcional)</Label>
                  <Input
                    value={form.kiosco_pago_movil_info}
                    onChange={(e) => setForm({...form, kiosco_pago_movil_info: e.target.value})}
                    placeholder="Notas visibles solo para admins"
                    data-testid="taquilla-kiosco-notes"
                  />
                </div>
              </div>
              {editing && form.kiosco_enabled && (
                <div className="text-[11px] font-semibold text-muted-foreground">
                  URL del kiosco: <span className="font-mono">/kiosco/{editing.id}</span>
                  <Button variant="link" size="sm" className="h-auto p-0 ml-2 text-primary" onClick={() => copyKioscoUrl(editing)}>Copiar enlace</Button>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => {
              if (!form.name?.trim() || !form.code?.trim()) { toast.error("Nombre y código son obligatorios"); return; }
              const payload = { ...form, name: form.name.trim(), code: form.code.trim() };
              if (!payload.kiosco_pin) delete payload.kiosco_pin;
              // El serial es inmutable: no reenviar en edición.
              if (editing) delete payload.hardware_serial;
              // La banca se resuelve en backend desde la agencia (si hay agencia asociada).
              if (payload.agencia_id) delete payload.banca_id;
              save.mutate(payload);
            }} disabled={save.isPending} data-testid="taquilla-form-submit">{editing ? "Guardar" : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Confirmar habilitar/deshabilitar */}
      <Dialog open={!!toggleTarget} onOpenChange={(v) => !v && setToggleTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="taquilla-toggle-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Power size={18}/> {toggleTarget?.active ? "Deshabilitar" : "Habilitar"} taquilla</DialogTitle></DialogHeader>
          <div className="text-sm text-muted-foreground">
            ¿Confirmas {toggleTarget?.active ? "deshabilitar" : "habilitar"} la taquilla <span className="font-semibold text-foreground">{toggleTarget?.name}</span>? Esta acción quedará registrada en auditoría.
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setToggleTarget(null)}>Cancelar</Button>
            <Button onClick={() => toggleStatus.mutate(toggleTarget.id)} disabled={toggleStatus.isPending} data-testid="taquilla-toggle-confirm">{toggleTarget?.active ? "Deshabilitar" : "Habilitar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {configFor && <EntityConfigDialog open={!!configFor} onClose={() => setConfigFor(null)} entityType="taquilla" entity={configFor}/>}
    </div>
  );
}
